/**
 * sachi-return-portal.js
 * Sachi Skin — Return Portal Client JS
 * Project: return-portal | Supabase: yupexdimsfuswwkouewp
 */

// ── CONFIG ────────────────────────────────────────────────────────────────────
const CONFIG = {
  supabaseUrl:     'https://yupexdimsfuswwkouewp.supabase.co',
  supabaseAnonKey: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inl1cGV4ZGltc2Z1c3d3a291ZXdwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjExMzYwNzQsImV4cCI6MjA3NjcxMjA3NH0.O5ljAVpLj__2UWztWB0StyqsAh_96zzzmykK8goBMWc',
  functions: {
    getOrders:      'https://yupexdimsfuswwkouewp.supabase.co/functions/v1/return-get-orders',
    submitReturn:   'https://yupexdimsfuswwkouewp.supabase.co/functions/v1/return-submit',
    submitTracking: 'https://yupexdimsfuswwkouewp.supabase.co/functions/v1/return-tracking',
  },
  portalBaseUrl: 'https://sachiskin.com/pages/sachi-return-portal',
}

// ── STATE ─────────────────────────────────────────────────────────────────────
const state = {
  isLoggedIn:          false,
  isEU:                false,
  loginUrl:            '/customer_authentication/login?return_to=/pages/sachi-return-portal',
  customerId:          null,
  customerEmail:       null,
  customerName:        null,
  customerFirstName:   null,
  orderNumber:         null, // EU no-login flow: order number entered for verification
  orders:        [],
  refundCount:   0,
  ltv:           0,
  orderCount:    0,
  eligible:      true,
  selectedItems: [], // [{ order_id, order_name, line_item_id, product_title, variant_title, quantity }]
  returnRef:     null,
  isIssueReport: false,
  submitting:    false, // M1: prevent double-submission
}

// ── INIT ──────────────────────────────────────────────────────────────────────
document.addEventListener('DOMContentLoaded', () => {
  // rp-config is always rendered by Liquid (logged-in or guest)
  const cfg = JSON.parse(document.getElementById('rp-config').textContent)
  state.isLoggedIn  = cfg.isLoggedIn === true
  state.isEU        = cfg.isEU === true
  state.loginUrl    = cfg.loginUrl || state.loginUrl
  if (state.isLoggedIn) {
    state.customerId        = cfg.customerId        || ''
    state.customerEmail     = cfg.customerEmail     || ''
    state.customerName      = cfg.customerName      || ''
    state.customerFirstName = cfg.customerFirstName || ''
  }

  // Handle tracking step via URL params (?step=tracking&ref=SR-xxx)
  const params = new URLSearchParams(window.location.search)
  const urlStep = params.get('step')
  const urlRef  = params.get('ref')

  if (urlStep === 'tracking' && urlRef) {
    state.returnRef = urlRef
    rpGoToStep('tracking')
    const badge = document.getElementById('rpTrackingBadge')
    if (badge) {
      badge.textContent = `Return Ref: ${urlRef}`
      badge.style.display = 'inline-block'
    }
    // Check if this ref already has tracking submitted — show locked state if so
    rpCheckTrackingStatus(urlRef)
    return
  }

  rpGoToStep('welcome')
  // Show stepper immediately on load at step 1
  const stepper = document.getElementById('rpStepper')
  if (stepper) stepper.style.display = 'block'

  // If user just came back after login (or is already logged in), skip welcome and load orders automatically
  if (state.isLoggedIn) {
    rpBeginReturn()
  }
})

// ── STEP NAVIGATION ───────────────────────────────────────────────────────────
function rpGoToStep(stepName) {
  document.querySelectorAll('.rp-step').forEach(el => el.classList.remove('is-active'))
  const target = document.getElementById(`step-${stepName}`)
  if (target) target.classList.add('is-active')

  // H9: Swap success copy based on whether this was an issue report
  if (stepName === 'success') {
    const body = document.getElementById('rpSuccessBody')
    if (body) {
      body.textContent = state.isIssueReport
        ? 'Our Ritual Companion will review your report and follow up by email within 1–2 business days.'
        : 'We will review and reply by email within 1–2 business days. Once approved, you will receive a link to share your return tracking number with us.'
    }
  }
  // M2: restore selection bar when returning to select step
  if (stepName === 'select') {
    rpUpdateSelectionBar()
  }
  // Show stepper on all main steps; hide on disqualified/tracking
  const stepper = document.getElementById('rpStepper')
  if (stepper) {
    const showStepper = ['welcome', 'verify', 'select', 'details', 'success'].includes(stepName)
    stepper.style.display = showStepper ? 'block' : 'none'
    if (showStepper) rpUpdateStepper(stepName)
  }

  window.scrollTo({ top: 0, behavior: 'smooth' })

  // M7: move keyboard focus to the first heading in the active step
  requestAnimationFrame(() => {
    const heading = target && target.querySelector('h1, h2')
    if (heading) {
      heading.setAttribute('tabindex', '-1')
      heading.focus({ preventScroll: true })
    }
  })
}

function rpUpdateStepper(stepName) {
  // Map page step names to 5-step stepper indices
  // welcome=0, signin/verify=1, select=2, details/reason=3, success/done=4
  const stepToIdx = { welcome: 0, verify: 1, select: 2, details: 3, success: 4 }
  const idx = stepToIdx[stepName] ?? 0
  // When logged in, signin step (idx 1) is already done when we're at select or beyond
  document.querySelectorAll('.rp-stepper__item').forEach((el, i) => {
    el.classList.toggle('is-done',   i < idx)
    el.classList.toggle('is-active', i === idx)
    // L6: aria-current on active step
    el.setAttribute('aria-current', i === idx ? 'step' : 'false')
  })
}

// ── WELCOME: BEGIN RETURN ─────────────────────────────────────────────────────
async function rpBeginReturn() {
  // Guest users are sent to login, unless they're visiting from the EU — EU withdrawal-right
  // rules require order lookup without a mandatory account, so they get the verify step instead.
  if (!state.isLoggedIn) {
    if (state.isEU) {
      rpGoToStep('verify')
      return
    }
    window.location.href = state.loginUrl
    return
  }

  const btn     = document.getElementById('rpBeginBtn')
  const loader  = document.getElementById('rpWelcomeLoader')
  const errEl   = document.getElementById('rpWelcomeError')

  btn.style.display    = 'none'
  loader.style.display = 'flex'
  errEl.style.display  = 'none'

  try {
    await rpLoadOrders()
    if (state.eligible) {
      rpGoToStep('select')
      // Personalise greeting
      const title = document.getElementById('rpSelectTitle')
      if (title && state.customerFirstName) {
      // L2: update subtitle, not the main title, to avoid losing context
      const subtitle = document.getElementById('rpSelectSubtitle')
      if (subtitle) subtitle.textContent = `Hello, ${state.customerFirstName}! Select the items you'd like to return.`
    }
    } else {
      rpGoToStep('disqualified')
    }
  } catch (err) {
    btn.style.display    = 'inline-flex'
    loader.style.display = 'none'
    errEl.textContent    = 'Unable to load your orders. Please refresh the page or contact customercare@sachiskin.com.'
    errEl.style.display  = 'block'
    console.error('rpLoadOrders error:', err)
  }
}

// ── LOAD ORDERS ───────────────────────────────────────────────────────────────
async function rpLoadOrders() {
  const res = await fetch(CONFIG.functions.getOrders, {
    method: 'POST',
    headers: {
      'Content-Type':  'application/json',
      'Authorization': `Bearer ${CONFIG.supabaseAnonKey}`,
    },
    body: JSON.stringify({
      customer_email: state.customerEmail,
      // EU no-login flow: order number doubles as identity verification server-side
      ...(state.orderNumber ? { order_number: state.orderNumber } : {}),
    }),
  })

  // Parse body first — the API returns a JSON error message even on non-2xx responses
  const data = await res.json().catch(() => ({}))
  if (!res.ok || !data.success) throw new Error(data.error || `Orders API error: ${res.status}`)

  state.orders             = data.orders             || []
  state.refundCount        = data.refundCount         || 0
  state.ltv                = data.ltv                 || 0
  state.orderCount         = data.orderCount          || 0
  state.eligible           = data.eligible
  state.customerName       = data.customer_name       || state.customerName
  state.customerFirstName  = data.customer_first_name || state.customerFirstName

  rpRenderOrders()
}

// ── VERIFY (EU no-login flow: email + order number) ──────────────────────────
async function rpSubmitVerify(e) {
  e.preventDefault()

  const email       = document.getElementById('rpVerifyEmail').value.trim()
  const orderNumber = document.getElementById('rpVerifyOrderNumber').value.trim()
  const errEl       = document.getElementById('rpVerifyError')
  const btn         = document.getElementById('rpVerifySubmitBtn')

  errEl.style.display = 'none'

  if (!email) {
    errEl.textContent   = 'Please enter the email address used for your order.'
    errEl.style.display = 'block'
    return
  }
  if (!orderNumber) {
    errEl.textContent   = 'Please enter your order number.'
    errEl.style.display = 'block'
    return
  }

  btn.disabled    = true
  btn.textContent = 'Finding your order…'

  state.customerEmail = email
  state.orderNumber   = orderNumber

  try {
    await rpLoadOrders()
    if (state.eligible) {
      rpGoToStep('select')
      const subtitle = document.getElementById('rpSelectSubtitle')
      if (subtitle && state.customerFirstName) {
        subtitle.textContent = `Hello, ${state.customerFirstName}! Select the items you'd like to return.`
      }
    } else {
      rpGoToStep('disqualified')
    }
  } catch (err) {
    errEl.textContent   = err.message || "We couldn't find an order matching that email and order number."
    errEl.style.display = 'block'
    console.error('rpSubmitVerify error:', err)
  } finally {
    btn.disabled    = false
    btn.textContent = 'Find my order'
  }
}

// ── RENDER ORDERS ─────────────────────────────────────────────────────────────
function rpRenderOrders() {
  const container = document.getElementById('rpOrdersList')
  if (!container) return

  if (!state.orders.length) {
    container.innerHTML = `
      <div class="rp-empty">
        <p>No orders found within the last 30 days.</p>
        <p style="margin-top:0.5rem;">
          Think this is an error? Contact
          <a href="mailto:customercare@sachiskin.com">customercare@sachiskin.com</a>.
        </p>
      </div>`
    return
  }

  container.innerHTML = state.orders.map(order => `
    <article class="rp-order-card" id="order-card-${order.id}">
      <div class="rp-order-card__header">
        <div>
          <p class="rp-order-card__name">${rpEscHtml(order.name)}</p>
          <p class="rp-order-card__meta">
            Placed ${rpFormatDate(order.created_at)} &middot; ${order.line_items.length} item${order.line_items.length !== 1 ? 's' : ''} &middot; £${parseFloat(order.total_price || 0).toFixed(2)}
          </p>
        </div>
        <button
          type="button"
          class="rp-btn-order-select"
          id="order-sel-btn-${order.id}"
          onclick="rpToggleOrder('${order.id}', this)"
        >
          Return whole order
        </button>
      </div>
      <ul class="rp-line-items">
        ${order.line_items.map(item => `
          <li class="rp-line-item">
            <input
              type="checkbox"
              class="rp-line-item__checkbox"
              id="item-chk-${item.id}"
              data-order-id="${order.id}"
              data-order-name="${rpEscHtml(order.name)}"
              data-line-item-id="${item.id}"
              data-product-title="${rpEscHtml(item.title)}"
              data-variant-title="${rpEscHtml(item.variant_title || '')}"
              data-quantity="${item.quantity}"
              aria-label="Select ${rpEscHtml(item.title)}"
              onchange="rpToggleItem(this)"
            >
            ${item.image
              ? `<img class="rp-line-item__image" src="${rpEscHtml(item.image)}" alt="${rpEscHtml(item.title)}" loading="lazy">`
              : `<div class="rp-line-item__image-placeholder"></div>`
            }
            <div class="rp-line-item__info">
              <p class="rp-line-item__name">${rpEscHtml(item.title)}</p>
              <p class="rp-line-item__variant">${item.variant_title ? rpEscHtml(item.variant_title) + ' &middot; ' : ''}Qty ${item.quantity}</p>
            </div>
            <span class="rp-line-item__price">£${parseFloat(item.price || 0).toFixed(2)}</span>
          </li>
        `).join('')}
      </ul>
    </article>
  `).join('')
}

// ── TOGGLE ORDER ("Return whole order" / "Deselect all") ──────────────────────
function rpToggleOrder(orderId, btn) {
  const order = state.orders.find(o => String(o.id) === String(orderId))
  if (!order) return

  const allSelected = order.line_items.every(item => {
    const chk = document.getElementById(`item-chk-${item.id}`)
    return chk && chk.checked
  })

  if (!allSelected) {
    // Select all items in this order
    order.line_items.forEach(item => {
      const chk = document.getElementById(`item-chk-${item.id}`)
      if (chk) chk.checked = true
      rpAddItem(orderId, order.name, item)
    })
    if (btn) { btn.textContent = 'Deselect all'; btn.classList.add('is-selected') }
  } else {
    // Deselect all items in this order
    order.line_items.forEach(item => {
      const chk = document.getElementById(`item-chk-${item.id}`)
      if (chk) chk.checked = false
    })
    state.selectedItems = state.selectedItems.filter(
      i => String(i.order_id) !== String(orderId)
    )
    if (btn) { btn.textContent = 'Return whole order'; btn.classList.remove('is-selected') }
  }

  rpUpdateSelectionBar()
}

// ── TOGGLE INDIVIDUAL LINE ITEM ───────────────────────────────────────────────
function rpToggleItem(chkEl) {
  const orderId    = chkEl.dataset.orderId
  const lineItemId = chkEl.dataset.lineItemId

  if (chkEl.checked) {
    rpAddItem(orderId, chkEl.dataset.orderName, {
      id:            lineItemId,
      title:         chkEl.dataset.productTitle,
      variant_title: chkEl.dataset.variantTitle,
      quantity:      parseInt(chkEl.dataset.quantity, 10),
    })
  } else {
    state.selectedItems = state.selectedItems.filter(
      i => !(String(i.order_id) === String(orderId) && String(i.line_item_id) === String(lineItemId))
    )
  }

  // Sync "Return whole order" button text
  const order = state.orders.find(o => String(o.id) === String(orderId))
  if (order) {
    const allChecked = order.line_items.every(item => {
      const chk = document.getElementById(`item-chk-${item.id}`)
      return chk && chk.checked
    })
    const selBtn = document.getElementById(`order-sel-btn-${orderId}`)
    if (selBtn) {
      selBtn.textContent = allChecked ? 'Deselect all' : 'Return whole order'
      selBtn.classList.toggle('is-selected', allChecked)
    }
  }

  rpUpdateSelectionBar()
}

function rpAddItem(orderId, orderName, item) {
  const exists = state.selectedItems.some(
    i => String(i.order_id) === String(orderId) && String(i.line_item_id) === String(item.id)
  )
  if (!exists) {
    state.selectedItems.push({
      order_id:      String(orderId),
      order_name:    orderName,
      line_item_id:  String(item.id),
      product_title: item.title,
      variant_title: item.variant_title || '',
      quantity:      item.quantity,
    })
  }
}

// M8: rpToggleItems() removed — dead code (old accordion, never called)

function rpUpdateSelectionBar() {
  const bar   = document.getElementById('rpSelectionBar')
  const text  = document.getElementById('rpSelectionBarText')
  const btn   = document.getElementById('rpContinueBtn')
  const count = state.selectedItems.length
  if (text) text.textContent = `${count} item${count !== 1 ? 's' : ''} selected`
  if (bar)  bar.classList.toggle('is-visible', count > 0)
  if (btn)  btn.style.display = count > 0 ? 'inline-flex' : 'none'
}

// ── PROCEED TO DETAILS ────────────────────────────────────────────────────────
function rpProceedToDetails() {
  const selectErrEl = document.getElementById('rpSelectError')
  if (state.selectedItems.length === 0) {
    // M3: replace alert() with inline error div
    if (selectErrEl) {
      selectErrEl.textContent = 'Please select at least one item to return.'
      selectErrEl.style.display = 'block'
    }
    return
  }
  if (selectErrEl) selectErrEl.style.display = 'none'

  const summary = document.getElementById('rpSelectedSummary')
  if (summary) {
    const itemNames = state.selectedItems.map(i => {
      const variant = i.variant_title ? ` / ${i.variant_title}` : ''
      return `${i.product_title}${variant} ×${i.quantity}`
    })
    summary.textContent = `Returning: ${itemNames.join(' · ')}`
    summary.style.display = 'block'
  }

  rpGoToStep('details')

  // Hide selection bar
  const bar = document.getElementById('rpSelectionBar')
  if (bar) bar.classList.remove('is-visible')
}

// ── SUBMIT RETURN ─────────────────────────────────────────────────────────────
async function rpSubmitReturn(e) {
  e.preventDefault()

  const reason      = document.getElementById('rpReason').value
  const description = document.getElementById('rpDescription').value.trim()
  const errEl       = document.getElementById('rpDetailsError')
  const btn         = document.getElementById('rpSubmitBtn')

  errEl.style.display = 'none'

  if (!reason) {
    errEl.textContent = 'Please select a reason for your return.'
    errEl.style.display = 'block'
    return
  }
  if (description.length < 20) {
    errEl.textContent = 'Please provide at least 20 characters of detail.'
    errEl.style.display = 'block'
    return
  }

  // M1: prevent double-submission
  if (state.submitting) return
  state.submitting = true

  btn.disabled    = true
  btn.textContent = 'Submitting…'

  try {
    const res = await fetch(CONFIG.functions.submitReturn, {
      method: 'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${CONFIG.supabaseAnonKey}`,
      },
      body: JSON.stringify({
        customer_email: state.customerEmail,
        customer_name:  state.customerName,
        selected_items: state.selectedItems,
        reason,
        description,
        issue_report:   false,
      }),
    })

    const data = await res.json()
    if (!res.ok || !data.success) throw new Error(data.error || 'Submission failed')

    state.returnRef     = data.return_ref
    state.isIssueReport = false

    const refEl = document.getElementById('rpSuccessRef')
    if (refEl) {
      refEl.textContent = `Reference: ${data.return_ref}`
      refEl.style.display = 'inline-block'
    }

    rpGoToStep('success')
  } catch (err) {
    errEl.textContent   = err.message || 'Something went wrong. Please try again.'
    errEl.style.display = 'block'
    btn.disabled        = false
    btn.textContent     = 'Submit Return Request'
    state.submitting    = false
    console.error('rpSubmitReturn error:', err)
  }
}

// ── TOGGLE ISSUE FORM (disqualified step) ────────────────────────────────────
function rpToggleIssueForm(checked) {
  const fields = document.getElementById('rpIssueFields')
  const btn    = document.getElementById('rpIssueSubmitBtn')
  if (fields) fields.style.display = checked ? 'flex' : 'none'
  if (btn)    btn.style.display    = checked ? 'inline-flex' : 'none'
}

// ── SUBMIT PRODUCT ISSUE (disqualified path) ──────────────────────────────────
async function rpSubmitIssue(e) {
  e.preventDefault()

  const issueType   = document.getElementById('rpIssueType').value
  const description = document.getElementById('rpIssueDescription').value.trim()
  const orderRef    = document.getElementById('rpIssueOrderRef').value.trim()
  const errEl       = document.getElementById('rpIssueError')
  const btn         = document.getElementById('rpIssueSubmitBtn')

  errEl.style.display = 'none'

  // H8: validate before submitting
  if (!issueType) {
    errEl.textContent   = 'Please select the type of issue.'
    errEl.style.display = 'block'
    return
  }
  if (description.length < 20) {
    errEl.textContent   = 'Please describe the issue in at least 20 characters.'
    errEl.style.display = 'block'
    return
  }

  // M1: prevent double-submission
  if (state.submitting) return
  state.submitting = true

  btn.disabled        = true
  btn.textContent     = 'Sending…'

  try {
    const res = await fetch(CONFIG.functions.submitReturn, {
      method: 'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${CONFIG.supabaseAnonKey}`,
      },
      body: JSON.stringify({
        customer_email:  state.customerEmail,
        customer_name:   state.customerName,
        selected_items:  [],
        reason:          issueType,
        description:     description,
        issue_order_ref: orderRef || null,
        issue_report:    true,
      }),
    })

    const data = await res.json()
    if (!res.ok || !data.success) throw new Error(data.error || 'Submission failed')

    state.isIssueReport = true
    rpGoToStep('success')
  } catch (err) {
    errEl.textContent   = err.message || 'Something went wrong. Please try again.'
    errEl.style.display = 'block'
    btn.disabled        = false
    btn.textContent     = 'Report Product Issue'
    state.submitting    = false
    console.error('rpSubmitIssue error:', err)
  }
}

// ── SUBMIT TRACKING ───────────────────────────────────────────────────────────
async function rpSubmitTracking(e) {
  e.preventDefault()

  const trackingNumber = document.getElementById('rpTrackingNumber').value.trim()
  const carrierName    = document.getElementById('rpCarrierName').value.trim()
  const errEl          = document.getElementById('rpTrackingError')
  const btn            = document.getElementById('rpTrackingSubmitBtn')

  errEl.style.display = 'none'

  if (!trackingNumber) {
    errEl.textContent   = 'Please enter your tracking number.'
    errEl.style.display = 'block'
    return
  }
  if (!carrierName) {
    errEl.textContent   = 'Please enter your carrier or shipping service.'
    errEl.style.display = 'block'
    return
  }
  if (!state.returnRef) {
    errEl.textContent   = 'Invalid return reference. Please check the link in your email.'
    errEl.style.display = 'block'
    return
  }

  btn.disabled    = true
  btn.textContent = 'Submitting…'

  try {
    const res = await fetch(CONFIG.functions.submitTracking, {
      method: 'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${CONFIG.supabaseAnonKey}`,
      },
      body: JSON.stringify({
        return_ref:      state.returnRef,
        tracking_number: trackingNumber,
        carrier_name:    carrierName,
        // H3: send email when available so server can verify ownership
        ...(state.customerEmail ? { customer_email: state.customerEmail } : {}),
      }),
    })

    const data = await res.json()
    if (!res.ok || !data.success) throw new Error(data.error || 'Submission failed')

    rpGoToStep('tracking-success')
  } catch (err) {
    errEl.textContent   = err.message || 'Something went wrong. Please try again.'
    errEl.style.display = 'block'
    btn.disabled        = false
    btn.textContent     = 'Submit Tracking Number'
    console.error('rpSubmitTracking error:', err)
  }
}

// ── CHECK TRACKING STATUS ─────────────────────────────────────────────────────
async function rpCheckTrackingStatus(returnRef) {
  try {
    const res = await fetch(CONFIG.functions.submitTracking, {
      method: 'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${CONFIG.supabaseAnonKey}`,
      },
      body: JSON.stringify({ return_ref: returnRef, check_only: true }),
    })
    const data = await res.json()
    if (data.status === 'tracking_submitted') {
      const lockedEl = document.getElementById('rpTrackingLocked')
      const formEl   = document.getElementById('rpTrackingForm')
      const numEl    = document.getElementById('rpTrackingLockedNumber')
      const carEl    = document.getElementById('rpTrackingLockedCarrier')
      if (lockedEl) lockedEl.style.display = 'block'
      if (formEl)   formEl.style.display   = 'none'
      if (numEl)    numEl.textContent  = data.tracking_number || '—'
      if (carEl)    carEl.textContent  = data.carrier_name    || '—'
    }
  } catch (err) {
    // Silent fail — don't block the tracking form if the status check fails
    console.error('rpCheckTrackingStatus error:', err)
  }
}

// ── UTILITIES ─────────────────────────────────────────────────────────────────
function rpFormatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  })
}

function rpEscHtml(str) {
  if (!str) return ''
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

// Expose functions called from inline onclick attrs
window.rpBeginReturn       = rpBeginReturn
window.rpSubmitVerify      = rpSubmitVerify
window.rpGoToStep          = rpGoToStep
window.rpToggleOrder       = rpToggleOrder
window.rpToggleItem        = rpToggleItem
window.rpToggleIssueForm   = rpToggleIssueForm
window.rpProceedToDetails  = rpProceedToDetails
window.rpSubmitReturn      = rpSubmitReturn
window.rpSubmitIssue       = rpSubmitIssue
window.rpSubmitTracking         = rpSubmitTracking
window.rpCheckTrackingStatus    = rpCheckTrackingStatus
